/**
 * Утилиты для работы с картами и адресами
 */

/**
 * Создает ссылку на Яндекс.Карты по тексту адреса
 * @param {string} address - Адрес для поиска
 * @param {number} zoom - Масштаб карты (по умолчанию 17)
 * @returns {string} URL для Яндекс.Карт
 */
export function createYandexMapLinkByAddress(address, zoom = 17) {
  if (!address || typeof address !== 'string') {
    return null;
  }
  
  const baseUrl = 'https://yandex.ru/maps/';
  const encodedAddress = encodeURIComponent(address);
  
  return `${baseUrl}?text=${encodedAddress}&z=${zoom}`;
}

/**
 * Создает ссылку на Яндекс.Карты с точной меткой по координатам
 * @param {number} lon - Долгота
 * @param {number} lat - Широта
 * @param {number} zoom - Масштаб карты (по умолчанию 17)
 * @returns {string} URL для Яндекс.Карт с меткой
 */
export function createYandexMapLinkByCoordinates(lon, lat, zoom = 17) {
  if (typeof lon !== 'number' || typeof lat !== 'number') {
    return null;
  }
  
  const baseUrl = 'https://yandex.ru/maps/';
  const ll = `${lon},${lat}`;
  const pt = `${lon},${lat},pm2rdm`; // pm2rdm = красная средняя метка
  
  return `${baseUrl}?ll=${encodeURIComponent(ll)}&z=${zoom}&pt=${encodeURIComponent(pt)}`;
}

/**
 * Создает ссылку на Яндекс.Карты для адреса объекта компании
 * Пытается использовать координаты, если доступны, иначе использует текст адреса
 * @param {string|object} address - Адрес (строка или объект с полями)
 * @param {object} coordinates - Координаты (объект с полями lat, lon)
 * @param {number} zoom - Масштаб карты (по умолчанию 17)
 * @returns {string|null} URL для Яндекс.Карт или null если адрес недоступен
 */
export function createCompanyMapLink(address, coordinates = null, zoom = 17) {
  // Если есть координаты, используем их
  if (coordinates && typeof coordinates.lon === 'number' && typeof coordinates.lat === 'number') {
    return createYandexMapLinkByCoordinates(coordinates.lon, coordinates.lat, zoom);
  }
  
  // Получаем текстовый адрес
  let addressText = '';
  
  if (typeof address === 'string') {
    addressText = address;
  } else if (address && typeof address === 'object') {
    // Пытаемся получить полный адрес из объекта
    addressText = address.full_address || 
                  address.value || 
                  address.address || 
                  '';
  }
  
  // Если адрес пустой, возвращаем null
  if (!addressText.trim()) {
    return null;
  }
  
  return createYandexMapLinkByAddress(addressText, zoom);
}

/**
 * Форматирует адрес для отображения
 * @param {string|object} address - Адрес для форматирования
 * @returns {string} Отформатированный адрес
 */
export function formatAddressText(address) {
  if (typeof address === 'string') {
    return address.trim() || 'Адрес не указан';
  }
  
  if (address && typeof address === 'object') {
    return address.full_address || 
           address.value || 
           address.address || 
           'Адрес не указан';
  }
  
  return 'Адрес не указан';
}

/**
 * Создает компонент кликабельного адреса
 * @param {string|object} address - Адрес
 * @param {object} coordinates - Координаты
 * @param {object} options - Дополнительные опции
 * @returns {object} Объект с данными для рендеринга
 */
export function createClickableAddressData(address, coordinates = null, options = {}) {
  const {
    zoom = 17,
    className = 'clickable-address',
    showMapIcon = true
  } = options;
  
  const addressText = formatAddressText(address);
  const mapLink = createCompanyMapLink(address, coordinates, zoom);
  
  return {
    text: addressText,
    link: mapLink,
    isClickable: !!mapLink,
    className: mapLink ? `${className} ${className}--clickable` : className,
    showMapIcon
  };
}
